/**
 * One-page workspace setup.
 *
 * Replaces the old 8-step skippable wizard. Two mandatory answers —
 * industry template and terminology — submitted together. Picking a template
 * prefills the terminology inputs, so the common path is "click a card,
 * click Finish".
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Check, Rocket } from 'lucide-react';

import { onboardingApi } from '@/features/auth/api';
import { SmartLoading } from '@/lib/ui';
import { useAuthStore } from '@/stores/auth-store';

/** Canonical singular keys — these must match the backend's TERMINOLOGY_KEYS. */
const TERM_FIELDS = [
  { key: 'worker', label: 'Workers', hint: 'Drivers, technicians, operators…' },
  { key: 'asset', label: 'Assets', hint: 'Trucks, vans, machines…' },
  { key: 'work_unit', label: 'Work units', hint: 'Trips, jobs, deliveries…' },
  { key: 'engagement', label: 'Engagements', hint: 'Sites, clients, routes…' },
] as const;

type TermKey = (typeof TERM_FIELDS)[number]['key'];
type Terminology = Record<TermKey, string>;

const EMPTY_TERMS: Terminology = {
  worker: '',
  asset: '',
  work_unit: '',
  engagement: '',
};

/**
 * Industry templates are seeded with plural keys (`workers`, `assets`, …)
 * while everything downstream reads the singular form. Fold them here so the
 * prefill lands in the right inputs.
 */
function termsFromTemplate(raw: Record<string, string> | undefined): Terminology {
  if (!raw) return { ...EMPTY_TERMS };
  const pick = (...names: string[]) => names.map((n) => raw[n]).find(Boolean) ?? '';
  return {
    worker: pick('worker', 'workers'),
    asset: pick('asset', 'assets'),
    work_unit: pick('work_unit', 'work_units'),
    engagement: pick('engagement', 'engagements'),
  };
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const tenantName = useAuthStore.getState().tenant?.tenantName ?? 'your workspace';
  const [error, setError] = useState<string | null>(null);
  const [templateSlug, setTemplateSlug] = useState<string | null>(null);
  const [terms, setTerms] = useState<Terminology>(EMPTY_TERMS);

  const state = useQuery({ queryKey: ['onboarding', 'state'], queryFn: onboardingApi.state });
  const templates = useQuery({
    queryKey: ['onboarding', 'templates'],
    queryFn: onboardingApi.templates,
  });

  // Already set up (e.g. a refresh after finishing) — don't strand the user here.
  useEffect(() => {
    if (state.data?.is_complete) navigate('/command-center', { replace: true });
  }, [state.data?.is_complete, navigate]);

  const setup = useMutation({
    mutationFn: () => onboardingApi.setup(templateSlug as string, terms),
    onSuccess: () => navigate('/command-center?onboarded=1', { replace: true }),
    onError: (err: Error) => setError(err.message),
  });

  const chooseTemplate = (slug: string, terminology: Record<string, string>) => {
    setTemplateSlug(slug);
    setTerms(termsFromTemplate(terminology));
    setError(null);
  };

  const missing = TERM_FIELDS.filter((f) => !terms[f.key].trim());
  const canSubmit = !!templateSlug && missing.length === 0 && !setup.isPending;

  if (state.isLoading || templates.isLoading) {
    return (
      <div className="grid min-h-screen place-items-center">
        <SmartLoading label="Loading setup…" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-10">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Rocket className="h-3.5 w-3.5" />
            Workspace setup
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Set up {tenantName}</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Two quick answers and you're in. Everything here can be changed later in Settings.
          </p>
        </header>

        <form
          className="space-y-10"
          onSubmit={(e) => {
            e.preventDefault();
            if (!templateSlug) {
              setError('Choose an industry type to continue.');
              return;
            }
            if (missing.length > 0) {
              setError(`Fill in: ${missing.map((m) => m.label).join(', ')}.`);
              return;
            }
            setError(null);
            setup.mutate();
          }}
        >
          {/* ── 1. Industry ─────────────────────────────────────────── */}
          <section>
            <SectionHeading
              step={1}
              title="What kind of operation is this?"
              subtitle="We'll set up your asset types, document checklists and salary defaults to match."
            />
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {(templates.data ?? []).map((t) => {
                const active = templateSlug === t.slug;
                return (
                  <button
                    key={t.slug}
                    type="button"
                    aria-pressed={active}
                    onClick={() => chooseTemplate(t.slug, t.terminology)}
                    className={`relative rounded-lg border p-4 text-left transition-all ${
                      active
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border bg-surface hover:border-primary/50 hover:bg-muted/40'
                    }`}
                  >
                    {active && (
                      <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3 w-3" />
                      </span>
                    )}
                    <div className="pr-6 font-medium">{t.name}</div>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {t.summary}
                    </p>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ── 2. Terminology ──────────────────────────────────────── */}
          <section>
            <SectionHeading
              step={2}
              title="What do you call things?"
              subtitle="These words replace the generic labels across the web panel and the mobile app."
            />
            {!templateSlug ? (
              <p className="mt-4 rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                Choose an industry type above and we'll fill these in for you.
              </p>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {TERM_FIELDS.map((f) => (
                  <div key={f.key}>
                    <label
                      htmlFor={`term-${f.key}`}
                      className="block text-sm font-medium text-foreground"
                    >
                      {f.label}
                    </label>
                    <input
                      id={`term-${f.key}`}
                      className="input mt-1.5"
                      value={terms[f.key]}
                      onChange={(e) => setTerms((t) => ({ ...t, [f.key]: e.target.value }))}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">{f.hint}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2.5 text-sm text-danger"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </p>
          )}

          <div className="flex items-center justify-between border-t border-border pt-6">
            <p className="text-xs text-muted-foreground">
              Both answers are required — there's nothing else to fill in.
            </p>
            <button type="submit" disabled={!canSubmit} className="btn-primary">
              {setup.isPending ? 'Setting up…' : 'Finish setup'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SectionHeading({
  step,
  title,
  subtitle,
}: {
  step: number;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
        {step}
      </span>
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
